// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — AuditTrail (M3: V·IANet Flow)
// Slide-in panel showing audit trail for a flowchart
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { FLOW_TOKENS as T } from '../shared/flowTokens';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import { request } from '../../../../shared/services/api';

// ─── Types ──────────────────────────────────────────────────────────────────

interface AuditEntry {
  id: string;
  accion: string;
  usuario: string;
  fecha: string;
  detalle?: string;
  comentario?: string;
}

interface AuditTrailProps {
  flowchartId: string;
  onClose: () => void;
}

// ─── Action label/color helpers ─────────────────────────────────────────────

function accionLabel(accion: string): string {
  switch (accion) {
    case 'CREAR':
      return 'Creado';
    case 'EDITAR':
      return 'Editado';
    case 'ENVIAR':
      return 'Enviado a revision';
    case 'APROBAR':
      return 'Aprobado';
    case 'RECHAZAR':
      return 'Rechazado';
    case 'PUBLICAR':
      return 'Publicado';
    case 'ARCHIVAR':
      return 'Archivado';
    default:
      return accion;
  }
}

function accionColor(accion: string): string {
  switch (accion) {
    case 'APROBAR':
      return T.color.statusApproved;
    case 'PUBLICAR':
      return T.color.statusApproved;
    case 'RECHAZAR':
      return T.color.statusRejected;
    case 'ENVIAR':
      return T.color.statusPending;
    default:
      return T.color.textSecondary;
  }
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function AuditTrail({ flowchartId, onClose }: AuditTrailProps) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!flowchartId) return;
    setLoading(true);
    setError(null);
    request<{ data?: AuditEntry[] } | AuditEntry[]>(`/flujogramas/edit/${flowchartId}/audit-trail`)
      .then((body) => {
        const list = Array.isArray(body) ? body : (body.data ?? []);
        setEntries(list);
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Error al cargar audit trail'),
      )
      .finally(() => setLoading(false));
  }, [flowchartId]);

  return (
    <div
      style={{
        width: 200,
        backgroundColor: T.color.surface,
        borderLeft: `1px solid ${T.color.border}`,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '8px 10px',
          borderBottom: `1px solid ${T.color.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <h3
          style={{
            fontSize: '8px',
            fontWeight: 700,
            fontFamily: T.font.family,
            color: T.color.textTertiary,
            textTransform: 'uppercase',
            letterSpacing: '0.7px',
            margin: 0,
          }}
        >
          AUDIT TRAIL
        </h3>
        <button
          onClick={onClose}
          title="Cerrar"
          style={{
            width: 18,
            height: 18,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            fontSize: 12,
            color: T.color.textTertiary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 3,
          }}
        >
          ×
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px' }}>
        {loading && <LoadingSpinner text="Cargando audit trail..." size="sm" />}
        {error && (
          <p style={{ fontSize: '8px', fontFamily: T.font.family, color: T.color.statusRejected }}>
            {error}
          </p>
        )}
        {!loading && !error && entries.length === 0 && (
          <p style={{ fontSize: '8px', fontFamily: T.font.family, color: T.color.textTertiary }}>
            Sin entradas de auditoria.
          </p>
        )}

        {/* Timeline */}
        {entries.map((entry, idx) => {
          const isLast = idx === entries.length - 1;
          const color = accionColor(entry.accion);

          return (
            <div
              key={entry.id ?? idx}
              style={{
                position: 'relative',
                paddingLeft: 14,
                paddingBottom: isLast ? 0 : 10,
                borderLeft: `1px solid ${T.color.border}`,
                marginLeft: 4,
              }}
            >
              {/* Dot */}
              <div
                style={{
                  position: 'absolute',
                  left: -4,
                  top: 2,
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  backgroundColor: color,
                  border: `1px solid ${color}`,
                }}
              />

              {/* Action label */}
              <div
                style={{
                  fontSize: '8px',
                  fontWeight: 700,
                  fontFamily: T.font.family,
                  color,
                  marginBottom: 1,
                }}
              >
                {accionLabel(entry.accion)}
              </div>

              {/* Date + user */}
              <div
                style={{
                  fontSize: '7px',
                  fontFamily: T.font.family,
                  color: T.color.textTertiary,
                  marginBottom: 1,
                }}
              >
                {new Date(entry.fecha).toLocaleString()} — {entry.usuario}
              </div>

              {/* Detail / comment */}
              {(entry.detalle || entry.comentario) && (
                <div
                  style={{
                    fontSize: '7px',
                    fontFamily: T.font.family,
                    color: T.color.textTertiary,
                    fontStyle: 'italic',
                    lineHeight: 1.3,
                  }}
                >
                  {entry.detalle || entry.comentario}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
